import {Directive, ElementRef, HostListener, Input} from '@angular/core';
import {NgControl} from '@angular/forms';
import {ValidationPattern, ValidatorService} from '@utils-lib';
import {TranslateService} from '@ngx-translate/core';

@Directive({
    selector: '[numeric]'
})
export class NumericDirective {

    private static readonly MAX_INTEGER = 2147483647;

    @Input()
    public decimal = null;
    @Input()
    public integer = null;
    @Input()
    public negativeAllowed = null;

    constructor(private readonly ngControl: NgControl,
                private readonly elementRef: ElementRef,
                private readonly validatorService: ValidatorService,
                private readonly translateService: TranslateService) {
    }

    private specialCharacters($event: KeyboardEvent) {
        if ($event.key === '-' && this.negativeAllowed !== null) {
            return true;
        }
        if ($event.key === '.') {
            return true;
        }
        if ($event.ctrlKey && $event.key.toLowerCase() === 'a') {
            return true;
        }
        if ($event.ctrlKey && $event.key.toLowerCase() === 'c') {
            return true;
        }
        return $event.ctrlKey && $event.key.toLowerCase() === 'x';
    }

    @HostListener('focus')
    public onFocus(): void {
        if (!this.ngControl || !this.ngControl.control || !this.ngControl.control.value) {
            return;
        }
        this.ngControl.control.setValue(this.ngControl.control.value.toString().replaceAll('.', ','));
        this.ngControl.control.updateValueAndValidity();
    }

    @HostListener('blur')
    public onBlur(): void {
        if (!this.ngControl || !this.ngControl.control || !this.ngControl.control.value) {
            return;
        }
        let value = +this.ngControl.control.value.toString().replaceAll('.', '').replaceAll(',', '.');
        const maxAllowed = (this.integer !== null) ? NumericDirective.MAX_INTEGER : Number.MAX_SAFE_INTEGER;
        const tooBigValidation = this.validatorService.required(this.translateService.instant('COMMON_UI.VALIDATION.INPUT_IS_TOO_LARGE_TO_HANDLE', {maxValue: maxAllowed}));
        if (value > maxAllowed) {
            this.elementRef.nativeElement.focus();
            this.ngControl.control.addValidators(tooBigValidation);
            value = null;
        }
        if (isNaN(value)) {
            value = null;
        }
        this.ngControl.control.setValue(value);
        this.ngControl.control.updateValueAndValidity();
        this.ngControl.control.removeValidators(tooBigValidation);
    }

    @HostListener('keyup', ['$event']) onKeyUp() {
        try {
            const firstChar = this.ngControl.value.substring(0, 1);
            const restString = this.ngControl.value.substring(1).replaceAll('-', '');
            this.ngControl.control.setValue(`${firstChar}${restString}`);
        } catch (e) {
        }
    }

    @HostListener('keypress', ['$event']) onKeyPress($event: KeyboardEvent) {
        if ($event.key.match(ValidationPattern.INTEGER) || this.specialCharacters($event)) {
            return;
        }
        if ((this.decimal !== null) && $event.key === ',' && !this.elementRef.nativeElement.value.includes(',')) {
            return;
        }
        $event.stopPropagation();
        $event.preventDefault();
    }

    @HostListener('paste', ['$event'])
    onPaste($event) {
        const pastedValue = $event.clipboardData?.getData('Text');
        const regex = (this.decimal !== null) ? ValidationPattern.NUMBER : ValidationPattern.INTEGER;
        if (!pastedValue.match(new RegExp(regex))) {
            $event.stopPropagation();
            $event.preventDefault();
        } else {
            setTimeout(() => {
                this.onFocus();
            });
        }
    }

}
